import React, { useEffect, useState } from 'react';
import { supabase } from '../supabaseClient';
import { FileText, Search, X, Calendar, User, ChevronLeft, Trash2, ArrowRight } from 'lucide-react';
import type { Profile, PatientSummary } from '../types';
import Critter from '../components/glyphs/Critter';

// מיפוי אווטארים (אותו מיפוי כמו בדשבורד)
export default function TherapistSummaries() {
  const [patients, setPatients] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Selected Patient & Summary State
  const [selectedPatient, setSelectedPatient] = useState<Profile | null>(null);
  const [summaries, setSummaries] = useState<PatientSummary[]>([]);
  const [summariesLoading, setSummariesLoading] = useState(false);
  const [viewingSummary, setViewingSummary] = useState<PatientSummary | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    fetchPatients();
  }, []);

  useEffect(() => {
    if (selectedPatient) {
      fetchSummaries(selectedPatient.id);
    } else {
      setSummaries([]);
    }
  }, [selectedPatient]);

  const fetchPatients = async () => {
    try {
      setLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('role', 'patient')
        .eq('therapist_id', user.id)
        .order('last_seen', { ascending: false });

      if (error) throw error;
      if (data) setPatients(data as Profile[]);
    } catch (err) {
      console.error('Error fetching patients:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchSummaries = async (patientId: string) => {
    try {
      setSummariesLoading(true);
      const { data, error } = await supabase
        .from('patient_summaries')
        .select('*')
        .eq('patient_id', patientId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setSummaries(data as PatientSummary[]);
    } catch (err) {
      console.error('Error fetching summaries:', err);
    } finally {
      setSummariesLoading(false);
    }
  };

  const activeDeleteRequest = () => {
     setShowDeleteConfirm(true);
  };

  const executeDelete = async () => {
    if (!viewingSummary) return;

    try {
      const { error } = await supabase
        .from('patient_summaries')
        .delete()
        .eq('id', viewingSummary.id);

      if (error) throw error;

      // Close modals and refresh list
      setShowDeleteConfirm(false);
      setViewingSummary(null);
      
      // Update local state immediately to remove from list without waiting for fetch
      setSummaries(prev => prev.filter(s => s.id !== viewingSummary.id));
    } catch (err) {
      console.error('Error deleting summary:', err);
      alert('שגיאה במחיקת הסיכום'); // alert for error is still acceptable or should be toast? User said "alert of warning", error is diff. keeping native for error or console.
    }
  };

  const filteredPatients = patients.filter(
    (patient) =>
      patient.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      patient.username?.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  return (
    <div className="flex flex-col h-full bg-background p-6 dir-rtl text-foreground relative">
      <header className="mb-6">
        <h1 className="text-2xl font-bold mb-2 flex items-center gap-2">
          <FileText className="text-primary" /> סיכומי מפגשים
        </h1>
        <p className="text-muted-foreground text-sm">בחר מטופל כדי לצפות בסיכומים</p>
      </header>

      {/* Search Bar */}
      <div className="relative mb-6">
        <input 
          type="text" 
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="חפש מטופל..." 
          className="w-full bg-card border border-border rounded-xl p-3 pr-10 text-foreground focus:outline-none focus:border-primary transition-colors"
        />
        <Search className="absolute top-3.5 right-3 text-muted-foreground" size={18} />
      </div>

      {/* Patients Grid (Tags) */}
      <div className="flex-1 overflow-y-auto pb-20">
        {loading ? (
           <p className="text-muted-foreground text-center mt-10">טוען מטופלים...</p>
        ) : filteredPatients.length > 0 ? (
          <div className="grid grid-cols-2 gap-3">
            {filteredPatients.map(patient => (
              <button
                key={patient.id}
                onClick={() => setSelectedPatient(patient)}
                className="flex items-center gap-3 p-3 bg-card border border-border rounded-2xl hover:border-primary hover:bg-muted transition-all text-right group"
              >
                <div className="w-10 h-10 rounded-full bg-muted border-2 border-border flex items-center justify-center text-lg shadow-sm group-hover:scale-110 transition-transform">
                   <Critter id={patient.avatar_id} size={34} />
                </div>
                <div className="overflow-hidden">
                  <h3 className="font-bold text-sm truncate text-foreground group-hover:text-foreground transition-colors">
                    {patient.full_name}
                  </h3>
                  <span className="text-[10px] text-muted-foreground flex items-center gap-1">
                     רמה {patient.level}
                  </span>
                </div>
              </button>
            ))}
          </div>
        ) : (
          <div className="text-center py-10 opacity-50 border-2 border-dashed border-border rounded-xl">
             <User size={32} className="mx-auto mb-2 opacity-50" />
             <p>לא נמצאו מטופלים</p>
          </div>
        )}
      </div>

      {/* Patient Summary List Modal */}
      {selectedPatient && (
        <div className="absolute inset-0 z-40 bg-background flex flex-col animate-in slide-in-from-bottom-5 duration-300">
          
          {/* Header */}
          <div className="p-4 bg-card border-b border-border flex items-center justify-between shadow-lg z-10">
            <div className="flex items-center gap-3">
               <button 
                 onClick={() => setSelectedPatient(null)}
                 className="p-2 -mr-2 text-muted-foreground hover:text-foreground rounded-full hover:bg-muted"
               >
                 <ChevronLeft size={24} />
               </button>
               <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-muted border border-border flex items-center justify-center text-lg">
                      <Critter id={selectedPatient.avatar_id} size={34} />
                  </div>
                  <div>
                    <h2 className="font-bold text-lg leading-tight">{selectedPatient.full_name}</h2>
                    <p className="text-xs text-muted-foreground">תיק אישי וסיכומים</p>
                  </div>
               </div>
            </div>
          </div>

          {/* Summaries List (Titles Only) */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-background">
             {summariesLoading ? (
               <div className="flex justify-center py-10">
                 <span className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
               </div>
             ) : summaries.length > 0 ? (
               summaries.map((summary) => (
                 <button 
                    key={summary.id} 
                    onClick={() => setViewingSummary(summary)}
                    className="w-full text-right bg-card border border-border rounded-xl p-4 shadow-sm hover:border-primary hover:bg-muted/80 transition-all group"
                 >
                    <div className="flex justify-between items-center">
                       <div>
                           <h3 className="font-bold text-foreground text-base mb-1 group-hover:text-primary transition-colors">
                               {summary.title}
                           </h3>
                           <span className="text-[10px] text-muted-foreground flex items-center gap-1">
                              <Calendar size={10} />
                              {new Date(summary.created_at).toLocaleDateString('he-IL', {
                                day: 'numeric',
                                month: 'short',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                           </span>
                       </div>
                       <ArrowRight size={18} className="text-muted-foreground group-hover:text-primary transition-colors" />
                    </div>
                 </button>
               ))
             ) : (
               <div className="flex flex-col items-center justify-center h-full text-muted-foreground opacity-60">
                  <FileText size={48} className="mb-4 text-muted-foreground" />
                  <p>אין סיכומים עדיין למטופל זה.</p>
                  <p className="text-xs">ניתן להוסיף סיכום דרך הדשבורד הראשי.</p>
               </div>
             )}
          </div>

          {/* Single Summary Detail View Modal */}
          {viewingSummary && (
              <div className="absolute inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
                  <div className="bg-card w-full max-w-2xl h-[80vh] rounded-2xl border border-border shadow-2xl flex flex-col animate-in zoom-in-95 duration-200">
                      
                      {/* Detail Header */}
                      <div className="p-4 border-b border-border flex justify-between items-center bg-card rounded-t-2xl">
                          <div>
                              <h3 className="text-xl font-bold text-foreground">{viewingSummary.title}</h3>
                              <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                                  <Calendar size={12} />
                                  {new Date(viewingSummary.created_at).toLocaleDateString('he-IL', {
                                      weekday: 'long',
                                      year: 'numeric',
                                      month: 'long',
                                      day: 'numeric',
                                      hour: '2-digit',
                                      minute: '2-digit'
                                  })}
                              </p>
                          </div>
                          <button 
                              onClick={() => setViewingSummary(null)}
                              className="text-muted-foreground hover:text-foreground p-2 hover:bg-muted rounded-full"
                          >
                              <X size={24} />
                          </button>
                      </div>

                      {/* Content */}
                      <div className="flex-1 overflow-y-auto p-6 bg-background/50">
                          <div className="prose prose-invert max-w-none whitespace-pre-wrap text-foreground leading-relaxed text-base">
                              {viewingSummary.content}
                          </div>
                      </div>

                      {/* Footer Actions */}
                      <div className="p-4 border-t border-border bg-card rounded-b-2xl relative">
                          {!showDeleteConfirm ? (
                            <div className="flex justify-between items-center">
                                <button 
                                  onClick={activeDeleteRequest}
                                  className="text-red-400 hover:text-red-300 hover:bg-red-900/20 px-4 py-2 rounded-xl text-sm font-bold flex items-center gap-2 transition-colors"
                                >
                                    <Trash2 size={16} /> מחק סיכום
                                </button>
                                
                                <button 
                                    onClick={() => setViewingSummary(null)}
                                    className="text-muted-foreground hover:text-foreground px-4 py-2 text-sm"
                                >
                                    סגור
                                </button>
                            </div>
                          ) : (
                            <div className="bg-red-900/20 border border-red-500/30 rounded-xl p-3 flex flex-wrap items-center justify-between gap-3 animate-in fade-in zoom-in-95 duration-200">
                                <span className="text-red-200 text-sm font-bold flex items-center gap-2 whitespace-nowrap">
                                   <Trash2 size={16} className="shrink-0" /> בטוח למחוק?
                                </span>
                                <div className="flex gap-2 w-full sm:w-auto">
                                   <button 
                                      onClick={() => setShowDeleteConfirm(false)}
                                      className="flex-1 sm:flex-none bg-muted hover:bg-muted text-foreground text-xs px-4 py-2 rounded-lg whitespace-nowrap transition-colors"
                                   >
                                      ביטול
                                   </button>
                                   <button 
                                      onClick={executeDelete}
                                      className="flex-1 sm:flex-none bg-red-600 hover:bg-red-700 text-white text-xs font-bold px-4 py-2 rounded-lg shadow-lg whitespace-nowrap transition-colors"
                                   >
                                      כן, אני בטוח
                                   </button>
                                </div>
                            </div>
                          )}
                      </div>
                  </div>
              </div>
          )}

        </div>
      )}
    </div>
  );
}
